// Privacy Erasure (PRD §12.10, §13.3) — admin-only.
//
// Flow:
//   1) Ввести email, нажать «Найти» → GET /privacy/recipient-data,
//      показать сводку (counts по dispatches / events / sessions / ...).
//   2) Если есть что удалять — кнопка «Удалить данные получателя»
//      с обязательной галочкой. Действие необратимо.
//   3) После erase — сводка обновляется на «удалённые числа», поле
//      становится readonly, чтобы случайно не нажать ещё раз; «Сброс»
//      возвращает на пустое состояние.
//
// Тексты ориентированы на менеджера по продажам, а не на разработчика:
// никаких dispatches/events/OTP/recipient_data_erased в видимых строках.

import { useState } from "react";
import { Link } from "react-router-dom";
import { humanizeApiError } from "../api/client";
import { type RecipientData, privacyApi } from "../api/privacy";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

type Phase =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "found"; data: RecipientData }
  | { kind: "erased"; data: RecipientData }
  | { kind: "error"; message: string };

function extractMessage(err: unknown): string {
  return humanizeApiError(err);
}

export function PrivacyErasurePage() {
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [erasing, setErasing] = useState(false);

  // Email-валидация — минимальная (наличие @ и точки): backend всё равно
  // проверит EmailStr. Здесь только чтобы не отправлять заведомо мусор.
  const isEmailLooksValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const trimmedEmail = email.trim();

  async function onSearch() {
    if (!isEmailLooksValid) return;
    setPhase({ kind: "loading" });
    setConfirmChecked(false);
    try {
      const data = await privacyApi.getRecipientData(trimmedEmail);
      setPhase({ kind: "found", data });
    } catch (err) {
      setPhase({ kind: "error", message: extractMessage(err) });
    }
  }

  async function onErase() {
    if (phase.kind !== "found") return;
    if (!confirmChecked) return;
    setErasing(true);
    try {
      const data = await privacyApi.eraseRecipient(phase.data.email);
      setPhase({ kind: "erased", data });
      setConfirmChecked(false);
    } catch (err) {
      setPhase({ kind: "error", message: extractMessage(err) });
    } finally {
      setErasing(false);
    }
  }

  function onReset() {
    setEmail("");
    setPhase({ kind: "idle" });
    setConfirmChecked(false);
  }

  const summary =
    phase.kind === "found" || phase.kind === "erased" ? phase.data : null;
  const hasAnythingToErase =
    phase.kind === "found" &&
    (phase.data.dispatches > 0 ||
      phase.data.dispatch_recipients > 0 ||
      phase.data.viewer_sessions > 0 ||
      phase.data.events > 0 ||
      phase.data.otp_codes > 0);

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <h1 className="text-2xl font-semibold">
          Удаление персональных данных получателя
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Если клиент попросил удалить свои данные (например, по требованию
          152-ФЗ или GDPR), здесь можно физически стереть всё, что мы храним
          по его email: историю просмотров документа, действия внутри
          документа, коды из писем и доступ из списков получателей. Сами
          карточки отправок и общая статистика по документам останутся —
          но email в них будет обезличен. Действие необратимо и записывается
          в журнал аудита.
        </p>
      </header>

      <Card data-testid="privacy-erasure-search">
        <CardHeader>
          <CardTitle>Найти данные по email</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label htmlFor="privacy-email">Email получателя</Label>
              <Input
                id="privacy-email"
                type="email"
                placeholder="client@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={phase.kind === "loading" || erasing}
                aria-describedby="privacy-email-help"
              />
              <p
                id="privacy-email-help"
                className="text-xs text-muted-foreground"
              >
                Регистр и пробелы не важны.
              </p>
            </div>
            <Button
              onClick={() => void onSearch()}
              disabled={
                !isEmailLooksValid || phase.kind === "loading" || erasing
              }
            >
              {phase.kind === "loading" ? "Поиск…" : "Найти"}
            </Button>
            <Button variant="outline" onClick={onReset} disabled={erasing}>
              Сброс
            </Button>
          </div>

          {phase.kind === "error" && (
            <p className="text-sm text-destructive" role="alert">
              {phase.message}
            </p>
          )}
        </CardContent>
      </Card>

      {summary && (
        <Card data-testid="privacy-erasure-summary">
          <CardHeader>
            <CardTitle>
              {phase.kind === "erased"
                ? `Удалено по ${summary.email}`
                : `Найдено по ${summary.email}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <SummaryRow
                label="Отправок документа"
                hint="Сколько персональных ссылок было отправлено на этот email."
                value={summary.dispatches}
              />
              <SummaryRow
                label="…из них уже обезличены ранее"
                hint="Email в этих отправках уже был стёрт прошлым удалением — повторно трогать нечего."
                value={summary.dispatches_already_erased}
              />
              <SummaryRow
                label="Доступов в списках получателей"
                hint="Записи в whitelist'ах разных отправок: где этот email был добавлен в «список разрешённых для просмотра»."
                value={summary.dispatch_recipients}
              />
              <SummaryRow
                label="Сессий просмотра документа"
                hint="Сколько раз клиент открывал документ — каждый визит это отдельная сессия."
                value={summary.viewer_sessions}
              />
              <SummaryRow
                label="Действий внутри документа"
                hint="Скроллы, переходы по страницам, клики по ссылкам, время на странице — всё, что записано аналитикой."
                value={summary.events}
              />
              <SummaryRow
                label="Кодов из писем"
                hint="Одноразовые коды доступа, которые мы присылали клиенту на email для входа в документ."
                value={summary.otp_codes}
              />
            </dl>

            {phase.kind === "found" && hasAnythingToErase && (
              <div className="space-y-3 border-t pt-4">
                <div className="rounded-md bg-muted/50 p-3 text-sm space-y-1">
                  <p className="font-medium">Что произойдёт при удалении:</p>
                  <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                    <li>
                      Email будет удалён из найденных отправок — карточки
                      отправок останутся, но имени и email клиента в них
                      больше не будет.
                    </li>
                    <li>
                      История просмотров и все действия клиента внутри
                      документа будут стёрты полностью.
                    </li>
                    <li>
                      Доступы из списков получателей и коды из писем — тоже
                      будут стёрты.
                    </li>
                    <li>
                      Общая статистика по документам и менеджерам не пропадёт
                      — она агрегирована и не привязана к конкретному
                      получателю.
                    </li>
                  </ul>
                </div>
                <p className="text-sm font-medium text-destructive">
                  Удаление необратимо. После подтверждения восстановить эти
                  данные нельзя.
                </p>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={confirmChecked}
                    onChange={(e) => setConfirmChecked(e.target.checked)}
                    className="mt-1"
                    data-testid="privacy-confirm"
                  />
                  <span>
                    Понимаю, что удаляю персональные данные клиента{" "}
                    <code className="font-mono">{summary.email}</code> без
                    возможности восстановления.
                  </span>
                </label>
                <Button
                  variant="destructive"
                  disabled={!confirmChecked || erasing}
                  onClick={() => void onErase()}
                  data-testid="privacy-erase-button"
                >
                  {erasing
                    ? "Удаление…"
                    : "Удалить данные получателя"}
                </Button>
              </div>
            )}

            {phase.kind === "found" && !hasAnythingToErase && (
              <p className="text-sm text-muted-foreground border-t pt-4">
                По этому email в системе нет персональных данных к удалению.
                Возможно, клиент никогда не получал документ через нас, либо
                его данные уже были удалены ранее.
              </p>
            )}

            {phase.kind === "erased" && (
              <p
                className="text-sm text-emerald-600 dark:text-emerald-400 border-t pt-4"
                role="status"
              >
                Данные удалены. Операция записана в{" "}
                <Link to="/audit-log" className="underline">
                  журнал аудита
                </Link>
                .
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SummaryRow({
  label,
  hint,
  value,
}: {
  label: string;
  hint?: string;
  value: number;
}) {
  return (
    <>
      <dt
        className="text-muted-foreground"
        title={hint}
        aria-label={hint ? `${label} — ${hint}` : undefined}
      >
        {label}
      </dt>
      <dd className="text-right font-mono tabular-nums">{value}</dd>
    </>
  );
}
